if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();
      
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });

      return result;
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        initialize();
        const target = this.getInputForEventTarget(event.target);

        // CRITICAL FIX: Handle auto-size selection IMMEDIATELY when color changes
        this.handleAutoSizeSelection(event.target);

        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
          data: {
            selectedOptionValues: this.selectedOptionValues,
            event,
            target
          }
        });
      });

      // Hook into the system's variant change event to auto-select after server update
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (data) => {
        this.handleVariantChangeComplete(data);
      });
    }

    // CRITICAL FIX: Handle auto-size selection IMMEDIATELY when color changes
    handleAutoSizeSelection(changedElement) {
      // Check if this is a color option change (first option group)
      const isColorChange = changedElement.closest('.m-product-option:first-child');

      if (!isColorChange) return; // Only handle color changes

      console.log('🎯 Color changed, auto-selecting size...');

      // Get the selected color value
      const selectedColorInput = changedElement.closest('.m-product-option:first-child').querySelector('input:checked');
      if (!selectedColorInput) {
        console.log('No color selected');
        return;
      }

      const selectedColor = selectedColorInput.value;
      console.log('Selected color:', selectedColor);

      // Find size inputs (second option group)
      const sizeInputs = document.querySelectorAll('.m-product-option:nth-child(2) input[type="radio"]');

      if (sizeInputs.length === 0) {
        console.log('No size options found');
        return;
      }

      // Get product variants data to check availability for this color
      let productVariants = null;

      // Try to get variants from theme products global object
      const productId = this.dataset.productId;
      if (window._themeProducts && window._themeProducts[productId]) {
        productVariants = window._themeProducts[productId].variants;
      }
      // Fallback: try to get variants from global window object
      else if (window.productVariants) {
        productVariants = window.productVariants;
      }
      // Fallback: try to get from product data script
      else {
        const productDataScript = document.querySelector('script[data-product-json]');
        if (productDataScript) {
          try {
            const productData = JSON.parse(productDataScript.textContent);
            productVariants = productData.variants;
          } catch (e) {
            console.log('Could not parse product data:', e);
          }
        }
      }

      if (!productVariants) {
        console.log('⚠️ Product variants data not found, using fallback logic');
        // Fallback to old logic
        this.handleAutoSizeSelectionFallback(sizeInputs);
        return;
      }

      // Find available sizes for this specific color
      const availableSizesForColor = productVariants
        .filter(variant => {
          // Check if variant matches the selected color and is available
          return variant.option1 === selectedColor && variant.available === true;
        })
        .map(variant => variant.option2) // Get the size values
        .filter(Boolean); // Remove null/undefined

      console.log('Available sizes for color', selectedColor, ':', availableSizesForColor);

      if (availableSizesForColor.length === 0) {
        console.log('⚠️ No available sizes found for color:', selectedColor);
        return;
      }

      // Check if any size is already selected and available for this color
      const currentlySelected = Array.from(sizeInputs).find(input =>
        input.checked && availableSizesForColor.includes(input.value)
      );

      if (currentlySelected) {
        console.log('Size already selected and available for this color:', currentlySelected.value);
        return; // Don't change if there's already a valid selection
      }

      // Find first available size for this color
      const firstAvailableSizeForColor = Array.from(sizeInputs).find(input =>
        availableSizesForColor.includes(input.value)
      );

      if (!firstAvailableSizeForColor) {
        console.log('No available size inputs found for color:', selectedColor);
        return;
      }

      // Clear all size selections first
      sizeInputs.forEach(input => {
        input.checked = false;
      });

      // Select the first available size for this color
      firstAvailableSizeForColor.checked = true;

      // Update the size display label - MULTIPLE SELECTOR ATTEMPTS
      let sizeSelectedLabel = null;

      // Try primary selector
      const sizeOptionGroup = document.querySelector('.m-product-option:nth-child(2)');
      if (sizeOptionGroup) {
        sizeSelectedLabel = sizeOptionGroup.querySelector('.option-label--selected');
      }

      // Fallback: try by data attribute
      if (!sizeSelectedLabel) {
        const sizeGroupByData = document.querySelector('.m-product-option[data-option-name="Size"]');
        if (sizeGroupByData) {
          sizeSelectedLabel = sizeGroupByData.querySelector('.option-label--selected');
        }
      }

      // Fallback: try generic size-related selectors
      if (!sizeSelectedLabel) {
        sizeSelectedLabel = document.querySelector('.m-product-option--button .option-label--selected') ||
                          document.querySelector('.m-product-option:nth-of-type(2) .option-label--selected');
      }

      if (sizeSelectedLabel) {
        sizeSelectedLabel.textContent = firstAvailableSizeForColor.value;
        console.log('✅ Updated size display to:', firstAvailableSizeForColor.value);
      } else {
        console.log('⚠️ Could not find size display label with any selector');
        console.log('Available size option groups:', document.querySelectorAll('.m-product-option').length);
      }

      // Trigger change event to update product info
      const changeEvent = new Event('change', { bubbles: true });
      firstAvailableSizeForColor.dispatchEvent(changeEvent);

      console.log('✅ Auto-selected first available size for color', selectedColor + ':', firstAvailableSizeForColor.value);
    }

    // Fallback method for when variant data is not available
    handleAutoSizeSelectionFallback(sizeInputs) {
      // Find first available size
      const firstAvailableSize = Array.from(sizeInputs).find(input =>
        !input.disabled && !input.classList.contains('disabled')
      );

      if (!firstAvailableSize) {
        console.log('No available sizes found');
        return;
      }

      // Clear all size selections first
      sizeInputs.forEach(input => {
        input.checked = false;
      });

      // Select the first available size
      firstAvailableSize.checked = true;

      // Update the size display label
      const sizeOptionGroup = document.querySelector('.m-product-option:nth-child(2)');
      const sizeSelectedLabel = sizeOptionGroup?.querySelector('.option-label--selected');

      if (sizeSelectedLabel) {
        sizeSelectedLabel.textContent = firstAvailableSize.value;
      }

      // Trigger change event to update product info
      const changeEvent = new Event('change', { bubbles: true });
      firstAvailableSize.dispatchEvent(changeEvent);

      console.log('✅ Auto-selected first available size (fallback):', firstAvailableSize.value);
    }

    // Handle variant change completion (after server update)
    handleVariantChangeComplete({ data }) {
      // Only auto-select if this is a color change (first option group changed)
      const colorInputs = document.querySelectorAll('.m-product-option:first-child input[type="radio"]');
      const sizeInputs = document.querySelectorAll('.m-product-option:nth-child(2) input[type="radio"]');

      // Check if we have both color and size options
      if (colorInputs.length === 0 || sizeInputs.length === 0) return;

      // Check if any size is already selected
      const hasSelectedSize = Array.from(sizeInputs).some(input => input.checked);

      // If no size selected, auto-select the first available one for the current color
      if (!hasSelectedSize) {
        const selectedColorInput = document.querySelector('.m-product-option:first-child input:checked');
        if (!selectedColorInput) return;

        const selectedColor = selectedColorInput.value;

        // Get product variants data to check availability for this color
        let productVariants = null;

        // Try to get variants from theme products global object
        const productId = this.dataset.productId;
        if (window._themeProducts && window._themeProducts[productId]) {
          productVariants = window._themeProducts[productId].variants;
        }
        // Fallback: try to get variants from global window object
        else if (window.productVariants) {
          productVariants = window.productVariants;
        }
        // Fallback: try to get from product data script
        else {
          const productDataScript = document.querySelector('script[data-product-json]');
          if (productDataScript) {
            try {
              const productData = JSON.parse(productDataScript.textContent);
              productVariants = productData.variants;
            } catch (e) {
              console.log('Could not parse product data in variant change completion:', e);
            }
          }
        }

        if (!productVariants) {
          // Fallback to old logic
          this.handleAutoSizeSelectionFallback(sizeInputs);
          return;
        }

        // Find available sizes for this specific color
        const availableSizesForColor = productVariants
          .filter(variant => variant.option1 === selectedColor && variant.available === true)
          .map(variant => variant.option2)
          .filter(Boolean);

        if (availableSizesForColor.length === 0) {
          console.log('⚠️ No available sizes found for color in variant change:', selectedColor);
          return;
        }

        // Find first available size for this color
        const firstAvailableSizeForColor = Array.from(sizeInputs).find(input =>
          availableSizesForColor.includes(input.value)
        );

        if (firstAvailableSizeForColor) {
          // Select the size
          firstAvailableSizeForColor.checked = true;

          // Update the display label
          const sizeOptionGroup = firstAvailableSizeForColor.closest('.m-product-option');
          const selectedLabel = sizeOptionGroup?.querySelector('.option-label--selected');
          if (selectedLabel) {
            selectedLabel.textContent = firstAvailableSizeForColor.value;
          }

          // Trigger change event to update product info
          const changeEvent = new Event('change', { bubbles: true });
          firstAvailableSizeForColor.dispatchEvent(changeEvent);

          console.log('✅ Auto-selected first available size for color in variant change completion:', selectedColor, firstAvailableSizeForColor.value);
        }
      }
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
  }

  customElements.define("variant-picker", VariantPicker);
}

// Variant Media Gallery Switcher
// Initialize - show only the media matching the initially selected variant
function initialize() {
  const variantInputs = document.querySelectorAll('.m-product-option__input');
  const mediaItems = document.querySelectorAll('.m-product-media--item');
  const mediaGallery = document.querySelector('.m-media-gallery__list');
  const checkedVariant = document.querySelector('.m-product-option__input:checked');
  
  console.log("Initializing variant picker:", {
    variantInputs: variantInputs.length,
    mediaItems: mediaItems.length,
    checkedVariant: checkedVariant?.value,
    availableColors: getAvailableColors()
  });

  // Filter on initial load based on selected variant
  if (checkedVariant) {
    console.log("Filtering images for initially selected variant:", checkedVariant.value);
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      filterMediaByVariant(checkedVariant.value);
    }, 50);
  } else {
    // If no variant is selected, try to filter by the first available color
    const firstColor = getAvailableColors()[0];
    if (firstColor) {
      console.log("No variant selected, using first available color:", firstColor);
      setTimeout(() => {
        filterMediaByVariant(firstColor);
      }, 50);
    }
  }
}

// Helper function to get available colors from variants
function getAvailableColors() {
  if (!window.productVariants) return [];
  return [...new Set(window.productVariants.map(v => v.option1).filter(Boolean))];
}

// Debug function to analyze image grouping
function analyzeImageGrouping() {
  const mediaItems = document.querySelectorAll('.m-product-media--item');
  const groupAnalysis = {};
  
  mediaItems.forEach(item => {
    const group = item.getAttribute('data-media-group');
    if (!groupAnalysis[group]) {
      groupAnalysis[group] = [];
    }
    groupAnalysis[group].push({
      index: item.getAttribute('data-index'),
      mediaId: item.getAttribute('data-media-id'),
      element: item
    });
  });
  
  console.log('🔍 Image Grouping Analysis:', groupAnalysis);
  console.log('📊 Images per group:', Object.keys(groupAnalysis).map(group => 
    `${group}: ${groupAnalysis[group].length} images`
  ));
  
  return groupAnalysis;
}

// Enhanced initialization with debugging
function initializeWithDebug() {
  console.log('🚀 Starting variant image system...');
  analyzeImageGrouping();
  hideOutOfStockVariantOptions();
  initialize();
}

// Hide out-of-stock variant options
function hideOutOfStockVariantOptions() {
  // Use Shopify's built-in availability check via CSS
  const unavailableNodes = document.querySelectorAll('.m-product-option--node__unavailable');
  console.log(`Found ${unavailableNodes.length} unavailable variant options`);
  
  unavailableNodes.forEach(node => {
    // Hide the entire option node
    node.style.display = 'none';
    node.classList.add('variant-out-of-stock');
    
    // Also disable the input if it exists
    const input = node.querySelector('input');
    if (input) {
      input.disabled = true;
    }
    
    console.log(`Hidden unavailable option:`, node.dataset.value || 'unknown');
  });
  
  // Also hide options marked as disabled
  const disabledInputs = document.querySelectorAll('input.disabled');
  disabledInputs.forEach(input => {
    const parentNode = input.closest('.m-product-option--node');
    if (parentNode) {
      parentNode.style.display = 'none';
      parentNode.classList.add('variant-out-of-stock');
      console.log(`Hidden disabled option:`, input.value);
    }
  });
}

// Filter media items based on selected variant
function filterMediaByVariant(variantValue) {
  try {
    const isMobile = window.innerWidth <= 768;
    
    // CRITICAL FIX: Only filter by COLOR (option1), not by size/other options
    let colorValue = null;
    
    // Get the currently selected color option (first option group is typically color)
    const colorOptionGroup = document.querySelector('.m-product-option:first-child');
    if (colorOptionGroup) {
      const selectedColorInput = colorOptionGroup.querySelector('input:checked');
      if (selectedColorInput) {
        colorValue = selectedColorInput.value.toLowerCase();
      }
    }
    
    // Fallback: use the variantValue only if it looks like a color
    if (!colorValue) {
      const colorKeywords = ['black', 'white', 'red', 'blue', 'green', 'yellow', 'pink', 'purple', 'orange', 'brown', 'gray', 'grey', 'beige', 'navy', 'cream', 'tan'];
      const isColor = colorKeywords.some(color => variantValue?.toLowerCase().includes(color));
      if (isColor) {
        colorValue = variantValue.toLowerCase();
      }
    }
    
    // If no color detected, don't filter (keep current state)
    if (!colorValue) {
      console.log('No color variant detected, skipping image filtering');
      return;
    }
    
    console.log('Filtering media for color:', colorValue);

    // DESKTOP: Handle media items
    if (!isMobile) {
      const mediaItems = document.querySelectorAll('.m-product-media--item');
      
      // First hide all items
      mediaItems.forEach(item => item.classList.add('custom-hidden-important'));

      // Find ALL items that match this color
      const matchingItems = [];
      mediaItems.forEach(item => {
        const itemGroup = item.getAttribute('data-media-group');
        if (itemGroup === colorValue) {
          matchingItems.push(item);
        }
      });
      
      // If no exact color match, try 'all' group
      if (matchingItems.length === 0) {
        mediaItems.forEach(item => {
          const itemGroup = item.getAttribute('data-media-group');
          if (itemGroup === 'all') {
            matchingItems.push(item);
          }
        });
      }
      
      // Show matching items or all if no matches
      if (matchingItems.length === 0) {
        mediaItems.forEach(item => item.classList.remove('custom-hidden-important'));
        console.log(`⚠️ No media found for color "${colorValue}", showing all media`);
      } else {
        matchingItems.forEach(item => item.classList.remove('custom-hidden-important'));
        console.log(`✅ Showing ${matchingItems.length} desktop media items for color: ${colorValue}`);
      }
    }

    // MOBILE: Handle swiper media items
    if (isMobile) {
      const mainMediaList = document.querySelector('.m-media-gallery__inner .m-media-gallery__list');
      const mainMediaItems = mainMediaList?.querySelectorAll('.m-product-media--item') || [];
      const swiperMediaList = document.querySelector('.m-product-media--slider__thumbnails__swiper-wrapper');
      const swiperMediaItems = swiperMediaList?.querySelectorAll('.slider__thumbnail_swiper-slide') || [];

      // Handle main media items
      mainMediaItems.forEach(item => item.classList.add('custom-hidden-important'));
      swiperMediaItems.forEach(item => item.classList.add('custom-hidden-important'));

      // Find ALL matching items for this color
      const matchingMainItems = [];
      const matchingSwiperItems = [];
      
      mainMediaItems.forEach(item => {
        if (item.getAttribute('data-media-group') === colorValue) {
          matchingMainItems.push(item);
        }
      });
      
      swiperMediaItems.forEach(item => {
        if (item.getAttribute('data-media-group') === colorValue) {
          matchingSwiperItems.push(item);
        }
      });
      
      // Fallback to 'all' group if no matches
      if (matchingMainItems.length === 0 && matchingSwiperItems.length === 0) {
        mainMediaItems.forEach(item => {
          if (item.getAttribute('data-media-group') === 'all') {
            matchingMainItems.push(item);
          }
        });
        swiperMediaItems.forEach(item => {
          if (item.getAttribute('data-media-group') === 'all') {
            matchingSwiperItems.push(item);
          }
        });
      }

      // Show matching items
      if (matchingMainItems.length === 0 && matchingSwiperItems.length === 0) {
        // Ultimate fallback - show all
        mainMediaItems.forEach(item => item.classList.remove('custom-hidden-important'));
        swiperMediaItems.forEach(item => item.classList.remove('custom-hidden-important'));
        console.log(`⚠️ No mobile media found for color "${colorValue}", showing all`);
      } else {
        matchingMainItems.forEach(item => item.classList.remove('custom-hidden-important'));
        matchingSwiperItems.forEach(item => item.classList.remove('custom-hidden-important'));
        // Automatically click the first matching swiper item
        if (matchingSwiperItems.length > 0) {
          matchingSwiperItems[0].click();
        }
        console.log(`✅ Showing ${matchingMainItems.length} main + ${matchingSwiperItems.length} thumbnail items for color: ${colorValue}`);
      }
    }
  } catch (err) {
    console.error("Error in filterMediaByVariant():", err);
  }
}

// Better initialization timing
document.addEventListener('DOMContentLoaded', function() {
  // Wait for theme scripts to load
  if (window.MinimogTheme && window.MinimogTheme.config) {
    initializeWithDebug();
  } else {
    // Fallback if theme isn't loaded yet
    setTimeout(() => {
      initializeWithDebug();
    }, 500);
  }
});

// Also initialize when variant picker is ready
window.addEventListener('load', function() {
  // Double-check initialization after everything is loaded
  setTimeout(() => {
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker && !window.variantImagesInitialized) {
      console.log('🔄 Secondary initialization check...');
      analyzeImageGrouping();
      window.variantImagesInitialized = true;
    }
  }, 100);
});

// Global debug function accessible from console
window.debugVariantImages = function() {
  console.log('=== VARIANT IMAGE DEBUG ===');
  console.log('Available variants:', window.productVariants);
  console.log('Available colors:', getAvailableColors());
  analyzeImageGrouping();
  console.log('Current selection:', document.querySelector('.m-product-option__input:checked')?.value);
};